import { useCallback, useEffect, useRef, useState } from 'react';

import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { MarketCountryCode } from '../../models/fundamental/types';
import { MarketState, MarketStateInstrumentSnapshot } from '../../models/market-data/types';
import { getNextMarketStateTransitionDelay } from '../market-state-transition.helpers';
import { calculateMarketState, convertToMarketState } from '../temporalDeadZoneFunctions';

import { useInterval } from './TimeoutHooks';

export const useMarketState = (countryCode: string = MarketCountryCode.UnitedStates) => {
  // eslint-disable-next-line max-len
  const [marketStateInstrumentSnapshot, setMarketStateInstrumentSnapshot] = useState<MarketStateInstrumentSnapshot | null>(null);
  const [marketState, setMarketState] = useState<MarketState | null>(null);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const applyMarketStateRef = useRef<() => void>(() => {});

  const applyMarketState = useCallback(() => {
    const newSnapshot = calculateMarketState(undefined, countryCode);
    const newState = convertToMarketState(newSnapshot);

    setMarketStateInstrumentSnapshot(newSnapshot);
    setMarketState(newState);
  }, [countryCode]);

  useEffect(() => {
    applyMarketStateRef.current = applyMarketState;
  }, [applyMarketState]);

  const clearTransitionTimeout = useCallback(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }, []);

  const scheduleTransitionUpdate = useCallback(() => {
    clearTransitionTimeout();
    const delay = getNextMarketStateTransitionDelay(countryCode);

    if (delay == null) {
      return;
    }

    transitionTimeoutRef.current = setTimeout(() => {
      applyMarketStateRef.current();
      scheduleTransitionUpdate();
    }, delay);
  }, [clearTransitionTimeout, countryCode]);

  const updateState = useCallback(() => {
    applyMarketState();
    scheduleTransitionUpdate();
  }, [applyMarketState, scheduleTransitionUpdate]);

  useEffect(() => {
    updateState();
  }, [countryCode, updateState]);

  useEffect(() => {
    const handleWindowFocus = () => updateState();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateState();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTransitionTimeout();
    };
  }, [clearTransitionTimeout, updateState]);

  useInterval(updateState, PredefinedMilisecondsTypeEnum.thirtySeconds);

  return { marketState, marketStateInstrumentSnapshot, refreshMarketState: updateState };
};
